import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { Box, Link, Stack, Tab, Tabs, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useAppSelector } from "../../app/hooks";
import {
  GET_COLOR_ANALYSES,
  GET_COLOR_GRADINGS,
  GET_DIAMOND_COLOR_RANKING,
  GET_DIAMS_WITH_IMAGE,
  GET_LOT_MATES,
  tokenOptions,
} from "../../backend/graphql";
import {
  byCalibration,
  isGraded,
  pictureLabel,
} from "../../backend/calibration";
import { backendURL } from "../../constants";
import { formatColorQuery } from "../../tools";
import AnalysisActions from "../widgets/AnalysisActions";
import GradingPrice from "./GradingPrice";
import ColorStateChips from "../widgets/ColorStateChips";
import DiamondProfile from "../widgets/DiamondProfile";
import SectionPaper from "../widgets/SectionPaper";
import SectionTitle from "../widgets/SectionTitle";
import SpreadBox, { SPREAD_AXES, unwrappedHue } from "../widgets/SpreadBox";
import ThumbnailCard from "../widgets/ThumbnailCard";
import useDocumentTitle, { stoneName } from "../widgets/useDocumentTitle";

const THUMBNAIL_SIZE = 140;
const SPREAD_WIDTH = 320;
// A color's patch beside its name: as tall as a small chip.
const PATCH_SIZE = 20;

// The color system graded. A picture is ranked against the Langerman colors too, but only the GIA
// grading is shown here, and only GIA colors are in the final grading.
const SYSTEM = "GIA";

// How many colors a picture's ranking lists; the window ranking lists as many.
const RANKED_COLORS = 32;

// The grading's own constants, as the worker grades: a color farther out than this many expected
// widths is simply far; the lot axis counts a lot with LOT_PRIOR mates of no color, on a log
// scale.
const WINDOW_DISTANCE_CAP = 3;
const LOT_PRIOR = 5;
const LOT_SCALE = 30;

// A color as the page names it: its id, for its own analysis, and its name.
type NamedColor = { ColorID: string; Color: string };

// One color of a picture's ranking.
type RankedColor = NamedColor & {
  Rank: number;
  // On the color's own active cells.
  Score: number;
  // On the whole color space; absent from the rankings stored before it was kept.
  FullScore?: number;
};

// One color of the final grading. The worker stores more of it — each line's certainty, what each
// axis adds — which the page does not show.
type GradedColor = NamedColor & {
  Place: number;
  Probability: number;
};

type Grading = {
  Colors: GradedColor[];
  Axes: string[];
};

const GRADING_HEADINGS = ["", "Color", "Probability", ""];
const AXIS_HEADINGS = ["Rank", "Color", "Score"];

// The final grading lists the colors down to the last that is at least this likely, or down to the
// stone's own color when that comes later, and never fewer than MIN_SHOWN.
const MIN_PROBABILITY = 0.001;
const MIN_SHOWN = 3;

const shownGrading = (grading: GradedColor[], stoneColor?: string) => {
  const likely = grading.filter(
    (entry) => entry.Probability >= MIN_PROBABILITY,
  ).length;
  const own = grading.findIndex((entry) => entry.Color === stoneColor) + 1;
  return grading.slice(0, Math.max(likely, own, MIN_SHOWN));
};

// The stone's own color, where its analysis read it.
type StonePosition = { L: number; C: number; H: number };

// What the page knows of a color beyond its name: its state in the grading, its patch, and its
// stored percentiles.
type ColorFacts = {
  state?: string;
  patchID?: string | null;
  percentiles?: any;
};

// A color's own page, in a new tab: the Color analysis page, which names its colors as Pricing by
// color does.
const openColorAnalysis = (color: string) =>
  window.open(
    "/analytics/coloranalysis?color=" +
      encodeURIComponent(formatColorQuery(SYSTEM, color)),
    "_blank",
  );

// A grid of the given number of columns, each as wide as its content.
const columnsSx = (count: number) => ({
  display: "grid",
  gridTemplateColumns: `repeat(${count}, max-content)`,
  columnGap: 2,
  rowGap: 0.25,
  // Centered rather than on the text's baseline: a color's name opens with its patch, and a
  // baseline taken past an image sits lower than the plain cells' beside it.
  alignItems: "center",
});

const Headings = ({ headings }: { headings: string[] }) => (
  <>
    {headings.map((heading, index) => (
      <Typography
        key={index}
        variant="caption"
        color="text.secondary"
        sx={{ textAlign: index === 1 ? "left" : "right" }}
      >
        {heading}
      </Typography>
    ))}
  </>
);

const ownSx = (own: boolean) => ({
  fontWeight: own ? 700 : 400,
  color: own ? "primary.main" : "text.primary",
});

// A right-aligned cell, the stone's own color picked out.
const Cell = ({
  own,
  children,
}: {
  own: boolean;
  children: React.ReactNode;
}) => (
  <Typography variant="body2" sx={{ ...ownSx(own), textAlign: "right" }}>
    {children}
  </Typography>
);

// A color's name in a list, after its patch drawn as small as a chip is tall: clicking it opens its
// color analysis, and in the final grading, pointing at it shows its spread. The stone's own color
// is picked out.
const ColorName = ({
  color,
  own,
  facts,
  onPoint,
}: {
  color: NamedColor;
  own: boolean;
  facts: Record<string, ColorFacts>;
  onPoint?: (color: NamedColor) => void;
}) => (
  <Typography
    variant="body2"
    sx={{
      ...ownSx(own),
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      "&:hover": { textDecoration: "underline" },
    }}
    onMouseEnter={() => onPoint?.(color)}
    onClick={() => openColorAnalysis(color.Color)}
  >
    {facts[color.ColorID]?.patchID ? (
      <img
        alt=""
        width={PATCH_SIZE}
        height={PATCH_SIZE}
        style={{ objectFit: "contain", borderRadius: 2 }}
        src={`${backendURL}/image/${facts[color.ColorID].patchID}`}
      />
    ) : (
      <Box sx={{ width: PATCH_SIZE, height: PATCH_SIZE }} />
    )}
    {formatColorQuery(SYSTEM, color.Color)}
    {/* The open-in-new glyph every link to another page carries. */}
    <OpenInNewIcon sx={{ fontSize: "1rem" }} />
  </Typography>
);

// The stone's final grading: every candidate color in the order of its probability of being the
// grade, with what the grading knows of the color.
const FinalGrading = ({
  grading,
  facts,
  stoneColor,
  onPoint,
}: {
  grading: GradedColor[];
  facts: Record<string, ColorFacts>;
  stoneColor?: string;
  onPoint: (color: NamedColor) => void;
}) => (
  <Box sx={columnsSx(GRADING_HEADINGS.length)}>
    <Headings headings={GRADING_HEADINGS} />
    {grading.map((entry) => {
      const own = entry.Color === stoneColor;
      return (
        <React.Fragment key={entry.Place}>
          <Cell own={own}>{entry.Place}</Cell>
          <ColorName color={entry} own={own} facts={facts} onPoint={onPoint} />
          <Cell own={own}>{(entry.Probability * 100).toFixed(1)}%</Cell>
          <ColorStateChips state={facts[entry.ColorID]?.state} compact />
        </React.Fragment>
      );
    })}
  </Box>
);

// The window of the color's expected width that holds its P5–P95 whole and comes as close as it can
// to the stone: of the positions that take the stone in, the nearest to centered on P5–P95; with the
// stone out of reach, the window pushed as far as it goes toward it.
const bestFit = (
  percentiles: number[],
  width: number,
  stone: number | null,
): number[] => {
  const [, low, , high] = percentiles;
  // The window's start may go from where its end meets P95 to where its start meets P5.
  const earliest = high - width;
  const latest = low;
  let start = (low + high - width) / 2;
  if (stone != null) {
    const from = Math.max(earliest, stone - width);
    const to = Math.min(latest, stone);
    if (from <= to) start = Math.min(Math.max(start, from), to);
    else start = stone > low ? latest : earliest;
  }
  return [start, start + width];
};

// The color pointed at, beside the final grading: its spread as the Color analysis page draws it —
// the members' intervals the worker stored on the color's analysis — with the stone's own position
// as a red line on each axis, and the window of the color's expected width that best fits the
// stone as a dashed rectangle.
const ColorPanel = ({
  color,
  stone,
}: {
  color: NamedColor;
  stone: StonePosition | null;
}) => {
  const token = useAppSelector((state) => state.auth.token);
  // While the next color loads, the last one stays drawn, so the page does not jump.
  const {
    data: currentData,
    previousData,
    loading,
  } = useQuery(GET_COLOR_ANALYSES, {
    ...tokenOptions(token),
    variables: {
      take: 1,
      skip: 0,
      filter: 'colorID == "' + color.ColorID + '"',
    },
  });
  const data = currentData ?? previousData;
  const analysis = data?.colorAnalyses?.items[0];
  const percentiles = analysis?.percentiles
    ? JSON.parse(analysis.percentiles)
    : null;

  // The stone's hue on the same turn as the color's stored percentiles.
  const position = (key: "L" | "C" | "H") => {
    if (!stone) return null;
    return key === "H" ? unwrappedHue(stone.H, percentiles.H[2]) : stone[key];
  };

  return (
    <Box
      sx={{
        width: SPREAD_WIDTH,
        position: "sticky",
        top: 16,
        alignSelf: "flex-start",
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
        {formatColorQuery(SYSTEM, color.Color)}
      </Typography>
      {percentiles ? (
        <Stack spacing={1}>
          {SPREAD_AXES.map((axis) => (
            <SpreadBox
              key={axis.key}
              label={axis.label}
              percentiles={percentiles[axis.key]}
              marker={position(axis.key)}
              outline={
                percentiles.ExpectedWidth
                  ? bestFit(
                      percentiles[axis.key],
                      percentiles.ExpectedWidth[axis.key],
                      position(axis.key),
                    )
                  : null
              }
              format={axis.format}
            />
          ))}
        </Stack>
      ) : (
        !loading && (
          <Typography variant="body2" color="text.secondary">
            No spread stored for this color
          </Typography>
        )
      )}
    </Box>
  );
};

// One color of an axis's ranking: its rank on the axis and the axis's value for it, as the grading
// reads it — higher is better on every axis.
type AxisRow = { color: NamedColor; rank: number; score: number };

// An axis's ranking, or why the axis says nothing for this stone.
type AxisTab = { label: string; rows: AxisRow[] | string };

// Ranks colors by their keys, the first key first, each key higher-better: colors equal on every
// key share the best rank they could have.
const ranked = (
  entries: { color: NamedColor; score: number; keys: number[] }[],
): AxisRow[] => {
  const sorted = [...entries].sort((a, b) => {
    for (let k = 0; k < a.keys.length; k++) {
      if (a.keys[k] !== b.keys[k]) return b.keys[k] - a.keys[k];
    }
    return 0;
  });
  let rank = 1;
  return sorted.map((entry, index) => {
    const previous = sorted[index - 1];
    if (previous && entry.keys.some((key, k) => key !== previous.keys[k])) {
      rank = index + 1;
    }
    return { color: entry.color, rank, score: entry.score };
  });
};

// One axis's ranking: rank, color and the axis's value.
const AxisTable = ({
  rows,
  facts,
  stoneColor,
}: {
  rows: AxisRow[];
  facts: Record<string, ColorFacts>;
  stoneColor?: string;
}) => (
  <Box sx={columnsSx(AXIS_HEADINGS.length)}>
    <Headings headings={AXIS_HEADINGS} />
    {rows.map((row) => {
      const own = row.color.Color === stoneColor;
      return (
        <React.Fragment key={row.color.ColorID}>
          <Cell own={own}>{row.rank}</Cell>
          <ColorName color={row.color} own={own} facts={facts} />
          <Cell own={own}>{row.score.toFixed(3)}</Cell>
        </React.Fragment>
      );
    })}
  </Box>
);

// A section of axes, one tab each, each tab a single ranking.
const AxisSection = ({
  title,
  tabs,
  facts,
  stoneColor,
  thumbnail,
}: {
  title: string;
  tabs: AxisTab[];
  facts: Record<string, ColorFacts>;
  stoneColor?: string;
  thumbnail?: React.ReactNode;
}) => {
  const [tab, setTab] = useState(0);
  const rows = tabs[tab]?.rows;
  return (
    <Box sx={{ mt: 2 }}>
      <SectionTitle>{title}</SectionTitle>
      <Stack
        direction="row"
        spacing={4}
        useFlexGap
        alignItems="flex-start"
        sx={{ mt: 1 }}
      >
        {thumbnail}
        <Box>
          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            sx={{ borderBottom: 1, borderColor: "divider", mb: 1 }}
          >
            {tabs.map((entry) => (
              <Tab key={entry.label} label={entry.label} />
            ))}
          </Tabs>
          {typeof rows === "string" ? (
            <Typography variant="body2" color="text.secondary">
              {rows}
            </Typography>
          ) : (
            <AxisTable rows={rows} facts={facts} stoneColor={stoneColor} />
          )}
        </Box>
      </Stack>
    </Box>
  );
};

// The window axis over every GIA color with a probable range: minus how far the stone's one color
// lies outside the range, summed over L, C and H in expected widths and capped, 0 inside; the
// colors the stone lies inside ranked by how central it is in each. The first RANKED_COLORS, as a
// picture's ranking lists that many.
const windowRanking = (
  stone: StonePosition | null,
  colors: NamedColor[],
  facts: Record<string, ColorFacts>,
): AxisRow[] | string => {
  if (!stone) return "The stone's color was not read";
  const entries = [];
  for (const color of colors) {
    const percentiles = facts[color.ColorID]?.percentiles;
    if (!percentiles?.ProbableRange || !percentiles?.ExpectedWidth) continue;
    let outside = 0;
    let toMedian = 0;
    for (const axis of ["L", "C", "H"] as const) {
      const median = percentiles[axis][2];
      const value = axis === "H" ? unwrappedHue(stone.H, median) : stone[axis];
      const [low, high] = percentiles.ProbableRange[axis];
      const width = percentiles.ExpectedWidth[axis];
      if (width > 0) {
        outside += Math.max(low - value, 0, value - high) / width;
        toMedian += Math.abs(value - median) / width;
      }
    }
    const score = -Math.min(outside, WINDOW_DISTANCE_CAP);
    entries.push({ color, score, keys: [score, -toMedian] });
  }
  return ranked(entries).slice(0, RANKED_COLORS);
};

// A lot axis: the colors the stone's certified lot-mates carry, each scored by its share of the lot
// — the lot counted with LOT_PRIOR mates of no color — on a log scale, 1 for a large unanimous lot.
const lotRanking = (
  label: string,
  number: number | null | undefined,
  mates: { color: string | null }[],
  colorIDs: Record<string, string>,
): AxisRow[] | string => {
  if (number == null) return `No ${label} number`;
  if (mates.length === 0)
    return `No other certified stone in ${label} ${number}`;
  const counts: Record<string, number> = {};
  for (const mate of mates) {
    if (mate.color) counts[mate.color] = (counts[mate.color] ?? 0) + 1;
  }
  const entries = Object.entries(counts).map(([color, count]) => {
    const share = count / (mates.length + LOT_PRIOR);
    const score = Math.log1p(LOT_SCALE * share) / Math.log1p(LOT_SCALE);
    return {
      color: { ColorID: colorIDs[color] ?? color, Color: color },
      score,
      keys: [score],
    };
  });
  return ranked(entries);
};

// A picture's axes, from its stored ranking: its stage rank (scored 1 / rank), and its full-space
// and own-cells scores, each below the picture's best.
const pictureTabs = (ranking: RankedColor[] | undefined): AxisTab[] => {
  if (!ranking) {
    return ["Stage ranking", "Full space", "Own cells"].map((label) => ({
      label,
      rows: "Not graded yet",
    }));
  }
  const byScore = (score: (entry: RankedColor) => number | undefined) => {
    const scored = ranking.filter((entry) => score(entry) != null);
    const best = Math.max(...scored.map((entry) => score(entry)!));
    return ranked(
      scored.map((entry) => {
        const value = score(entry)! - best;
        return { color: entry, score: value, keys: [value] };
      }),
    );
  };
  return [
    {
      label: "Stage ranking",
      rows: ranking.map((entry) => ({
        color: entry,
        rank: entry.Rank,
        score: 1 / entry.Rank,
      })),
    },
    { label: "Full space", rows: byScore((entry) => entry.FullScore) },
    { label: "Own cells", rows: byScore((entry) => entry.Score) },
  ];
};

// The grading analysis of a stone: its header, then its final grading — the GIA colors it may be,
// in the order of their probability, each with how well the grading knows it, and pointing at one
// shows how the stone's color sits in its spread — and, for a developer who asks for them, each
// axis's ranking: the axes not tied to a picture first, then each picture's.
const GradingAnalysis = () => {
  const { id } = useParams();
  const token = useAppSelector((state) => state.auth.token);
  const isDeveloper = useAppSelector((state) => state.auth.isDeveloper);
  const [pointed, setPointed] = useState<NamedColor | null>(null);
  const [showAxes, setShowAxes] = useState(false);
  const { loading, error, data } = useQuery(GET_DIAMS_WITH_IMAGE, {
    ...tokenOptions(token),
    variables: { take: 1, skip: 0, filter: 'iD == "' + id + '"' },
  });
  const { data: rankingData } = useQuery(GET_DIAMOND_COLOR_RANKING, {
    ...tokenOptions(token),
    variables: { take: 1, skip: 0, filter: 'diamondID == "' + id + '"' },
  });
  const { data: gradingsData } = useQuery(GET_COLOR_GRADINGS, {
    ...tokenOptions(token),
    variables: { take: 10000, skip: 0 },
  });

  const diamond = data?.diamonds?.items[0];
  // The stone's lots: the certified stones carrying its flat or its melee number.
  const lotClauses = [
    diamond?.flat != null ? `flat == ${diamond.flat}` : null,
    diamond?.melee != null ? `melee == ${diamond.melee}` : null,
  ].filter((clause) => clause != null);
  const { data: matesData } = useQuery(GET_LOT_MATES, {
    ...tokenOptions(token),
    skip: !isDeveloper || lotClauses.length === 0,
    variables: {
      take: 10000,
      skip: 0,
      filter:
        'source == "Langerman" && isGIACertified == true && color != null && (' +
        lotClauses.join(" || ") +
        ")",
    },
  });
  useDocumentTitle(stoneName(diamond), "Grading analysis");

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error.message}</p>;
  if (!diamond) return <p>Not found!</p>;

  const analysis = rankingData?.diamondAnalyses?.items[0];
  // { system: { picture name: ranking } }
  const rankings = analysis?.colorRanking
    ? JSON.parse(analysis.colorRanking)
    : {};
  const stored = analysis?.finalGrading
    ? JSON.parse(analysis.finalGrading)
    : null;
  // A grading stored before the axes is not read: the stone is regraded at the next fit.
  const finalGrading: Grading | null = stored?.Axes ? stored : null;
  // Each GIA color's state in the grading, its patch and its percentiles, by color id.
  const gradedColors = (gradingsData?.colorAnalyses?.items ?? []).filter(
    (item: any) => item.color?.system === SYSTEM,
  );
  const facts: Record<string, ColorFacts> = Object.fromEntries(
    gradedColors.map((item: any) => [
      item.colorID,
      {
        state: item.grading ? JSON.parse(item.grading).State : undefined,
        patchID: item.colorPatchID,
        percentiles: item.percentiles ? JSON.parse(item.percentiles) : null,
      },
    ]),
  );
  const namedColors: NamedColor[] = gradedColors.map((item: any) => ({
    ColorID: item.colorID,
    Color: item.color.color,
  }));
  const colorIDs: Record<string, string> = Object.fromEntries(
    namedColors.map((color) => [color.Color, color.ColorID]),
  );
  const stone: StonePosition | null =
    analysis?.oKL != null
      ? { L: analysis.oKL, C: analysis.oKC, H: analysis.oKH }
      : null;
  const pictures = [...(diamond.images ?? [])]
    .filter((image: any) => isGraded(image.calibration))
    .sort(byCalibration);
  const mates: { color: string | null; flat: number; melee: number }[] = (
    matesData?.diamonds?.items ?? []
  ).filter((mate: any) => mate.id !== diamond.id);

  const shown = finalGrading
    ? shownGrading(finalGrading.Colors, diamond.color)
    : [];
  // The spread shown beside the final grading: the stone's official color until another is
  // pointed at, or the first color when the official one is not listed.
  const panelColor =
    pointed ?? shown.find((entry) => entry.Color === diamond.color) ?? shown[0];

  const transversalTabs: AxisTab[] = [
    { label: "Window", rows: windowRanking(stone, namedColors, facts) },
    {
      label: "Flat",
      rows: lotRanking(
        "flat",
        diamond.flat,
        mates.filter((mate) => mate.flat === diamond.flat),
        colorIDs,
      ),
    },
    {
      label: "Melee",
      rows: lotRanking(
        "melee",
        diamond.melee,
        mates.filter((mate) => mate.melee === diamond.melee),
        colorIDs,
      ),
    },
  ];

  return (
    <Stack spacing={1} useFlexGap>
      <DiamondProfile data={diamond} page="grading" />
      <SectionPaper>
        {/* Analyze grades every picture again from what its calibration stored, and the stone. */}
        <AnalysisActions
          diamondID={diamond.id}
          canAnalyze={pictures.length > 0}
        />
        <SectionTitle>Final grading</SectionTitle>
        <Stack
          direction="row"
          spacing={4}
          useFlexGap
          flexWrap="wrap"
          alignItems="flex-start"
          sx={{ mt: 1 }}
        >
          {finalGrading ? (
            <FinalGrading
              grading={shown}
              facts={facts}
              stoneColor={diamond.color}
              onPoint={setPointed}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {analysis?.isIgnored
                ? "Not graded: a box, a set or a chameleon"
                : "Not graded yet"}
            </Typography>
          )}
          {panelColor && <ColorPanel color={panelColor} stone={stone} />}
        </Stack>

        {finalGrading && (
          <GradingPrice
            diamondID={diamond.id}
            certified={!!diamond.isGIACertified}
            stoneColor={diamond.color}
            carat={diamond.carat}
          />
        )}

        {/* What the final grading is drawn from, for developers: each axis's ranking, the axes not
            tied to a picture first, then each picture's. */}
        {isDeveloper && (
          <Link
            component="button"
            variant="body2"
            sx={{ mt: 2, display: "block" }}
            onClick={() => setShowAxes(!showAxes)}
          >
            {showAxes ? "Show less" : "Show more..."}
          </Link>
        )}
        {isDeveloper && showAxes && (
          <>
            <AxisSection
              title="Transversal"
              tabs={transversalTabs}
              facts={facts}
              stoneColor={diamond.color}
            />
            {pictures.map((image: any) => (
              <AxisSection
                key={image.id}
                title={pictureLabel(image, diamond.source)}
                tabs={pictureTabs(rankings[SYSTEM]?.[image.origin])}
                facts={facts}
                stoneColor={diamond.color}
                thumbnail={
                  <ThumbnailCard
                    size={THUMBNAIL_SIZE}
                    src={`${backendURL}/image/thumbnail/${image.id}`}
                    alt={pictureLabel(image, diamond.source)}
                    href={`/analytics/fromimage/${image.id}`}
                  />
                }
              />
            ))}
          </>
        )}
      </SectionPaper>
    </Stack>
  );
};

export default GradingAnalysis;
